import { __awaiter } from "tslib";
import '@/styles/main.scss';
import polyfils from '@/utils/polyfils';
import Vue from 'vue';
import App from './App.vue';
import router from './router';
import store from './store';
import vuetify from '@/plugins/vuetify';
import commonBus from '@/commonBus';
import { COMMON_BUS_COMPONENTS_LIST } from '@/constants/commonBusComponentsList';
import Icon from '@/components/global/Icon/index.vue';
import VueScrollComponent from '@/components/global/scrollComponent/index.vue';
import VuetifyInputmask from '@/plugins/vuetify-inputmask';
import transitionDependentFocus from '@/plugins/transition-dependent-focus';
import filters from '@/vue-utils/filters';
import prototypeMethods from '@/vue-utils/prototype-methods';
import numberBox from '@/components/global/number-box/index.vue';
import scrollLoader from '@/components/global/scrollLoader/index.vue';
import serverErrors from '@/components/global/serverErrors/index.vue';
import loadWidgetErrorShell from '@/components/global/loadWidgetErrorShell/index.vue';
import phoneinput from '@/components/global/phoneinput/index.vue';
import autocomplete from '@/components/global/autocomplete/index.vue';
import { handleChangePositions, getWidgetPositions } from '@/utils/dropDataHandlers';
import axios from 'axios';
import { getServiceConfigProperty, isTrustedEnvironment } from '@/constants/services';
import VueI18n from 'vue-i18n';
import baseTranslation from '@/assets/rawData/i18n/index.json';
import { resolveSearchParams } from '@/vue-utils/search-params';
import customAlert from '@whitelist/wl-old-library/components/global/alert/index.vue';
import customSelect from '@whitelist/wl-old-library/components/global/customSelect/index.vue';
import popup from '@whitelist/wl-old-library/components/global/popup/index.vue';
// @ts-ignore
import * as Sentry from '@sentry/vue';
import { initChaport } from '@/utils/chaport';
Vue.config.productionTip = false;
const additionalPopupsRequires = require.context('./../src/components/', true, /\.popup-chunk\.vue$/i);
const additionalPopups = additionalPopupsRequires.keys().reduce((acc, curr) => {
    const parsed = require('./../src/components/' + curr.substring(2));
    acc[parsed.default.extendOptions.name] = parsed.default;
    return acc;
}, {});
const Popup = popup;
Popup.options.components = Object.assign(Object.assign({}, Popup.options.components), additionalPopups);
for (const method of Object.values(polyfils)) {
    method();
}
COMMON_BUS_COMPONENTS_LIST.forEach(item => {
    commonBus[item.name] = {};
    commonBus.mainState.components[item.name] = {
        dropped: false
    };
});
Vue.component('popup', Popup);
Vue.component('vue-scroll-component', VueScrollComponent);
Vue.component('number-box', numberBox);
Vue.component('custom-select', customSelect);
Vue.component('load-widget-error-shell', loadWidgetErrorShell);
Vue.component('custom-alert', customAlert);
Vue.component('phoneinput', phoneinput);
Vue.component('serverErrors', serverErrors);
Vue.component('autocomplete', autocomplete);
Vue.component('scroll-loader', scrollLoader);
Vue.use(VueI18n);
const SENTRY_TRACE_RATE = 0.1;
Sentry.init({
    Vue,
    dsn: 'https://dcf5686e4c512da74e963e9a52e583fb@sentry.prod.k8s.whitelist.capital/35',
    integrations: [
        Sentry.browserTracingIntegration({ router }),
        Sentry.replayIntegration({
            maskAllText: true,
            blockAllMedia: true
        })
    ],
    // Performance Monitoring
    tracesSampleRate: SENTRY_TRACE_RATE,
    // Session Replay
    replaysSessionSampleRate: SENTRY_TRACE_RATE,
    replaysOnErrorSampleRate: SENTRY_TRACE_RATE // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
});
export const i18n = new VueI18n({
    locale: 'ru',
    fallbackLocale: 'ru',
    messages: baseTranslation,
    pluralizationRules: {
        /**
         * @param choice {number} индекс выбора, переданный в $tc: `$tc('path.to.rule', choiceIndex)`
         * @param choicesLength {number} общее количество доступных вариантов
         * @returns финальный индекс для выбора соответственного варианта слова
         */
        ru: function (choice, choicesLength) {
            // this === VueI18n экземпляра, так что свойство locale также существует здесь
            if (choice === 0) {
                return 0;
            }
            const teen = choice > 10 && choice < 20;
            const endsWithOne = choice % 10 === 1;
            if (choicesLength < 4) {
                return (!teen && endsWithOne) ? 1 : 2;
            }
            if (!teen && endsWithOne) {
                return 1;
            }
            if (!teen && choice % 10 >= 2 && choice % 10 <= 4) {
                return 2;
            }
            return (choicesLength < 4) ? 2 : 3;
        }
    }
});
Vue.use(VuetifyInputmask);
Vue.use(transitionDependentFocus);
Vue.prototype.$throwInGateway = function (componentName) {
    return __awaiter(this, void 0, void 0, function* () {
        const vm = this;
        const $el = document.querySelector('#' + COMMON_BUS_COMPONENTS_LIST.find(item => item.name === componentName).id);
        const remote = require('electron').remote;
        const { app } = require('electron').remote;
        const BrowserWindow = remote.BrowserWindow;
        const options = {
            parent: vm.$commonBus.main,
            height: $el.clientHeight,
            width: $el.clientWidth + 16,
            webPreferences: {
                nodeIntegration: true,
                enableRemoteModule: true,
                contextIsolation: false
            },
            frame: false
        };
        const positions = getWidgetPositions(componentName);
        if (positions) {
            for (const [key, v] of Object.entries(positions)) {
                options[key] = v;
            }
        }
        vm.$commonBus[componentName] = new BrowserWindow(options);
        vm.$commonBus[componentName].webContents.executeJavaScript(`window.gateway.val = '${componentName}'`);
        const modalPath = `file://${__dirname}/index.html`;
        if (process.env.NODE_ENV === 'development') {
            vm.$commonBus[componentName].loadURL(`${location.origin}`);
            // if (!process.env.IS_TEST) this.$commonBus[componentName].webContents.openDevTools()
        }
        else {
            vm.$commonBus[componentName].loadURL(modalPath);
        }
        vm.$commonBus.mainState.components[componentName].dropped = true;
        vm.$commonBus[componentName].on('closed', () => {
            delete vm.$commonBus[componentName];
            vm.$commonBus.mainState.components[componentName].dropped = false;
        });
        // @ts-ignore
        app.on('windowmoved', (response) => {
            for (const [key, value] of Object.entries(response)) {
                handleChangePositions(key, value.x, value.y);
            }
        });
    });
};
Vue.prototype.$repareFromGateway = function (componentName) {
    return __awaiter(this, void 0, void 0, function* () {
        this.$commonBus[componentName].close();
    });
};
Vue.prototype.$throwInBrowserGateway = function (componentName) {
    const vm = this;
    const $el = document.querySelector('#' + COMMON_BUS_COMPONENTS_LIST.find(item => item.name === componentName).id);
    const widgetPositions = getWidgetPositions(componentName);
    let futures = 'directories=no,titlebar=no,toolbar=no,location=no,status=no,menubar=no,scrollbars=no,resizable=no';
    if ($el) {
        futures += ',width=' + ($el.clientWidth + 16);
        futures += ',height=' + ($el.clientHeight + 23);
    }
    futures += ',left=' + widgetPositions.x;
    futures += ',top=' + widgetPositions.y;
    vm.$commonBus[componentName] = window.open(location.origin + `/gateway?component=${componentName}`, '_blank', futures);
    vm.$commonBus.mainState.components[componentName].dropped = true;
    // vm.$commonBus[componentName].addEventListener('beforeunload', () => {
    //   vm.$commonBus.mainState.components[componentName].dropped = false
    // })
    vm.$commonBus[componentName].onload = function () {
        this.onkeydown = checkKeycode;
        function checkKeycode(e) {
            if (e.keyCode === 116 || (e.ctrlKey && e.keyCode === 82))
                e.preventDefault();
            if (e.ctrlKey || e.shiftKey)
                e.preventDefault();
        }
    };
    vm.$commonBus[componentName].onunload = function (e) {
        setTimeout(() => {
            if (vm.$commonBus[componentName].closed)
                vm.$commonBus.mainState.components[componentName].dropped = false;
        }, 100);
    };
    this.$commonBus[componentName].addEventListener('windowmoved', (event) => {
        for (const [key, value] of Object.entries(event.detail)) {
            handleChangePositions(key, value.x, value.y);
        }
    });
};
Vue.prototype.$repareFromBrowserGateway = function (componentName) {
    return __awaiter(this, void 0, void 0, function* () {
        this.$commonBus[componentName].close();
    });
};
Vue.prototype.getServiceConfigProperty = getServiceConfigProperty;
Vue.prototype.isTrustedEnvironment = isTrustedEnvironment;
for (const [name, f] of Object.entries(filters)) {
    Vue.filter(name, f);
}
for (const [name, declaration] of Object.entries(prototypeMethods)) {
    Vue.prototype[name] = declaration;
}
initChaport();
window.gateway = {
    value: '',
    get val() {
        return this.value;
    },
    set val(value) {
        router.replace({ name: 'gateway', query: { component: value } });
        this.value = value;
    }
};
Vue.component('Icon', Icon);
Vue.mixin({
    computed: {
        isGateway() {
            return this.$route.name === 'gateway';
        },
        isErrorPage() {
            return this.$route.name === 'NamedErrorPage' || this.$route.name === 'errorPage';
        },
        miw() {
            const vm = this;
            return (width) => vm.$root.windowWidth >= width;
        },
        maw() {
            const vm = this;
            return (width) => vm.$root.windowWidth < width;
        },
        mih() {
            const vm = this;
            return (height) => vm.$root.windowHeight >= height;
        },
        mah() {
            const vm = this;
            return (height) => vm.$root.windowHeight < height;
        }
    },
    methods: {
        closeWin() {
            window.close();
        }
    }
});
new Vue({
    data: () => ({
        commonBus: {},
        windowHeight: 0,
        windowWidth: 0
    }),
    router,
    store,
    i18n,
    vuetify: vuetify,
    render: h => h(App),
    mounted() {
        setTimeout(() => {
            store.commit('user/setIsAuthenticated', true);
        }, 100);
        if (process.platform !== 'browser') {
            router.replace({ name: 'mainPage' });
            const dragRegion = document.createElement('div');
            dragRegion.classList.add('drag-region');
            dragRegion.style.display = 'none';
            // document.body.appendChild(dragRegion)
            document.addEventListener('keydown', (event) => {
                if (event.key === 'Control')
                    dragRegion.style.display = 'block';
            });
            document.addEventListener('keyup', (event) => {
                if (event.key === 'Control')
                    dragRegion.style.display = 'none';
            });
        }
    },
    created() {
        window.appInstance = this;
        const vm = this;
        vm.$vuetify.theme.dark = true;
        this.commonBus = commonBus;
        Vue.prototype.$commonBus = this.commonBus;
        Vue.prototype.isDropped = (name) => { var _a; return (_a = this.commonBus.mainState.components[name]) === null || _a === void 0 ? void 0 : _a.dropped; };
        vm.windowWidth = window.innerWidth;
        vm.windowHeight = window.innerHeight;
        window.addEventListener('resize', () => {
            vm.windowWidth = window.innerWidth;
            vm.windowHeight = window.innerHeight;
        });
        resolveSearchParams(vm);
        setTimeout(() => {
            axios.get('https://ipinfo.io').then(response => {
                let dial = response.data.country.toLowerCase();
                vm.$store.commit('user/setUserCountryISO', dial);
                switch (dial) {
                    case 'ru':
                        dial = 'ru';
                        break;
                    case 'ua':
                        dial = 'ru';
                        break;
                    case 'by':
                        dial = 'ru';
                        break;
                    case 'kz':
                        dial = 'ru';
                        break;
                    default:
                        dial = 'en';
                }
            }).catch(response => {
                vm.$store.commit('user/setUserCountryISO', 'ru');
            });
        }, 100);
    }
}).$mount('#app');
